import {gql, useMutation} from '@apollo/client';
import {Pda} from './components/Pda';
import {Header} from './components/Header';
import { ArrivalRelatedScanComponent, HandleScanEventResult } from './components/ArrivalRelatedScanWeightComponent';
import { showBarCodeError, showTrackingNumberError } from '../../utils/BarCodeUtil';
import { isTrackingNumber } from '../../utils/BarCodeUtil';
import {NavBar, Switch, Popup, Dialog} from "antd-mobile";
import {ArrowLeftOutlined, SettingOutlined} from "@ant-design/icons";
import React, { useState } from "react";
import { t } from '../../i18n';
import { getNavBarContainerStyle } from '../../utils/deviceUtils';
import BluetoothScaleSelector from './components/BluetoothScaleSelector';
import { useBluetoothScaleStore } from '../../store/bluetoothScaleStore';
import { getClientReporter } from '../../utils/clientReport/clientReporterRegistry';
import { ClientReportOpType } from '../../utils/clientReport/clientReportTypes';
import { safeJsonStringify, truncateToLength } from '../../utils/clientReport/clientReportUtils';


const HANDLE_PICKUP_ARRIVAL_SCAN_EVENT = gql`
   mutation HandleWhsPickupArrivalScanEvent($barCode: String!, $weightKg: Float, $clientReportKey: String) {
     success: handleWhsPickupArrivalScanEvent(barCode: $barCode, weightKg: $weightKg, clientReportKey: $clientReportKey)
   }
`

interface PageBodyProps {
    showSettings: boolean;
    setShowSettings: (show: boolean) => void;
}

const PageBody = ({ showSettings, setShowSettings }: PageBodyProps) => {
    const pickupArrivalClientReporter = getClientReporter(ClientReportOpType.PICKUP_ARRIVAL_SCAN);
    const [handlePickupArrivalScanEvent] = useMutation<HandleScanEventResult>(HANDLE_PICKUP_ARRIVAL_SCAN_EVENT);

    // 使用全局蓝牙状态
    const { isConnected: isBluetoothConnected, currentWeightData, weighSimultaneously, setWeighSimultaneously } = useBluetoothScaleStore();

    const handleScanEvent = async (barCode: string): Promise<HandleScanEventResult> => {
        const clientReportKey = pickupArrivalClientReporter.createReportKey(barCode);
        void pickupArrivalClientReporter.onScanTriggered(clientReportKey, barCode);

        if (!isTrackingNumber(barCode)) {
            showTrackingNumberError(barCode);
            void pickupArrivalClientReporter.onIntercepted(clientReportKey, `invalid_tracking_number:${barCode}`);
            return {success: false};
        }

        // 如果开启了同时称重，需要校验蓝牙连接和重量数据
        let weightKg: number | undefined = undefined;
        if (weighSimultaneously) {
            if (!isBluetoothConnected) {
                // 弹窗提示未连接蓝牙，不抛出异常
                const message = t('pda.pages.pickUpArrival.bluetoothNotConnected');
                Dialog.alert({
                    content: t('pda.pages.pickUpArrival.bluetoothNotConnected'),
                    confirmText: t('pda.common.confirm'),
                });
                void pickupArrivalClientReporter.onIntercepted(clientReportKey, message);
                return {success: false};
            }
            if (!currentWeightData || currentWeightData.weight <= 0) {
                // 弹窗提示无法获取重量
                const message = t('pda.pages.pickUpArrival.weightNotAvailable');
                Dialog.alert({
                    content: message,
                    confirmText: t('pda.common.confirm'),
                });
                void pickupArrivalClientReporter.onIntercepted(clientReportKey, message);
                return {success: false};
            }

            // 检查 rawData 的第一个字符是否为负号
            if (currentWeightData.rawData && currentWeightData.rawData.charAt(0) === '-') {
                // 弹窗提示重量为负数
                const message = t('pda.pages.pickUpArrival.negativeWeightError');
                Dialog.alert({
                    content: message,
                    confirmText: t('pda.common.confirm'),
                });
                void pickupArrivalClientReporter.onIntercepted(clientReportKey, message);
                return {success: false};
            }

            // 单位转换：lb转kg，kg保持不变
            weightKg = currentWeightData.unit.toLowerCase() === 'lb'
                ? currentWeightData.weight * 0.453592
                : currentWeightData.weight;

            // 保留3位小数
            weightKg = Math.round(weightKg * 1000) / 1000;
        }

        void pickupArrivalClientReporter.onRequest(clientReportKey, { barCode, weightKg });
        let responseRecorded = false;
        let responseData: any = null;
        try {
            const {data} = await handlePickupArrivalScanEvent({
                variables: {
                    barCode,
                    weightKg: weightKg,
                    clientReportKey
                }
            });
            const success = data?.success || false;
            responseData = { success };
            try {
                await pickupArrivalClientReporter.onResponse(clientReportKey, responseData);
                responseRecorded = true;
            } catch (_) {
                // IndexedDB 写入失败，直接触发上报
                void pickupArrivalClientReporter.reportDirectly(clientReportKey, {
                    responseAtMs: Date.now(),
                    responseResult: truncateToLength(safeJsonStringify(responseData), 300)
                });
                responseRecorded = true; // 标记已处理，避免 finally 重复上报
            }
            return {success};
        } catch (e) {
            responseData = { error: String(e) };
            try {
                await pickupArrivalClientReporter.onResponse(clientReportKey, responseData);
                responseRecorded = true;
            } catch (_) {
                // IndexedDB 写入失败，直接触发上报
                void pickupArrivalClientReporter.reportDirectly(clientReportKey, {
                    responseAtMs: Date.now(),
                    responseResult: truncateToLength(safeJsonStringify(responseData), 300)
                });
                responseRecorded = true; // 标记已处理，避免 finally 重复上报
            }
            throw e;
        } finally {
            if (!responseRecorded) {
                try {
                    await pickupArrivalClientReporter.onResponse(clientReportKey, responseData || {});
                } catch (_) {
                    // IndexedDB 写入失败，直接触发上报
                    void pickupArrivalClientReporter.reportDirectly(clientReportKey, {
                        responseAtMs: Date.now(),
                        responseResult: responseData ? truncateToLength(safeJsonStringify(responseData), 300) : ''
                    });
                }
            }
        }
    };


    return (
        <>
            {/* 设置弹出层 */}
            <Popup
                visible={showSettings}
                onMaskClick={() => setShowSettings(false)}
                position="right"
                bodyStyle={{ width: '100vw', height: '100vh', padding: 0 }}
            >
                <div className="w-full h-full flex flex-col">
                    {/* 设置标题栏 */}
                    <div className="flex items-center px-4 py-3 bg-white shadow-sm">
                        <div
                            className="cursor-pointer"
                            onClick={() => setShowSettings(false)}
                        >
                            <ArrowLeftOutlined className="text-xl" />
                        </div>
                        <h2 className="flex-1 text-center text-lg font-semibold">{t('pda.pages.pickUpArrival.settings')}</h2>
                        <div className="w-6"></div> {/* 占位平衡 */}
                    </div>

                    {/* 设置内容 - 与主页面相同的背景和间距 */}
                    <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
                        {/* 同时称重开关 */}
                        <div className="bg-white p-4 rounded">
                            <div className="flex justify-between items-center">
                                <span className="text-base font-medium">{t('pda.pages.pickUpArrival.weighSimultaneously')}</span>
                                <Switch
                                    checked={weighSimultaneously}
                                    onChange={setWeighSimultaneously}
                                />
                            </div>
                        </div>

                        {/* 蓝牙电子秤组件 - 始终显示，方便用户提前连接 */}
                        <BluetoothScaleSelector />
                    </div>
                </div>
            </Popup>

            {/* 扫描组件 */}
            <ArrivalRelatedScanComponent
                value1Placeholder={t('pda.pages.pickUpArrival.trackingNO')}
                value1ColumnTitle={t('pda.pages.pickUpArrival.trackingNO')}
                handleScanEvent={handleScanEvent}
                weighSimultaneously={weighSimultaneously}
                currentWeight={currentWeightData}
                isBluetoothConnected={isBluetoothConnected}
                successExtraFields={weighSimultaneously ? [{ title: t('pda.pages.pickUpArrival.weight'), key: 'weight' }] : undefined}
            />
        </>
    );
}

export default function PickupArrivalPage() {
    const [showSettings, setShowSettings] = useState(false);

    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            {/* 顶部导航栏 */}
            <div
                {...getNavBarContainerStyle('relative')}
                style={{ ...(getNavBarContainerStyle().style || {}), paddingTop: 0 }} // ⬅️ 覆盖掉白条
            >
                <Header title={t('pda.pages.pickUpArrival.pickUpArrivalScan')}/>
                <div
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center cursor-pointer"
                    onClick={() => setShowSettings(true)}
                >
                    <SettingOutlined className="text-xl text-gray-600" />
                </div>
            </div>
            <Pda/>
            <PageBody showSettings={showSettings} setShowSettings={setShowSettings} />
        </div>
    );
}